/**
 * ServiceCard - Base card for subscription service monitoring
 *
 * Shows service name, alert badge, usage bar, and deep link.
 */

import { Card, Tag, Progress, Typography, Space } from "antd";
import type { AlertLevel } from "@/services/subscriptionsService";
import { MaterialIcon } from "@/components/MaterialIcon";

const { Text } = Typography;

interface UsageMetric {
  label: string;
  value: number | string;
  quota?: number;
  unit?: string;
  alertLevel?: AlertLevel;
}

export interface ServiceCardProps {
  title: string;
  subtitle?: React.ReactNode;
  alertLevel: AlertLevel;
  deepLink?: string;
  metrics?: UsageMetric[];
  extra?: React.ReactNode;
  children?: React.ReactNode;
}

const alertConfig: Record<AlertLevel, { color: string; label: string; tagColor: string }> = {
  ok: { color: "#52c41a", label: "OK", tagColor: "success" },
  warning: { color: "#faad14", label: "Warning", tagColor: "warning" },
  critical: { color: "#ff4d4f", label: "Critical", tagColor: "error" },
};

function formatMetricValue(value: number | string, unit?: string): string {
  if (typeof value === "string") return value;
  if (unit === "bytes") {
    if (value >= 1e12) return `${(value / 1e12).toFixed(1)} TB`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)} GB`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)} MB`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(0)} KB`;
    return `${value} B`;
  }
  return value.toLocaleString() + (unit ? ` ${unit}` : "");
}

export default function ServiceCard({
  title,
  subtitle,
  alertLevel,
  deepLink,
  metrics,
  extra,
  children,
}: ServiceCardProps) {
  const alert = alertConfig[alertLevel];

  const cardTitle = (
    <div>
      <Space>
        <span>{title}</span>
        <Tag color={alert.tagColor}>{alert.label}</Tag>
      </Space>
      {subtitle && <div style={{ marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  const cardExtra = (
    <Space>
      {extra}
      {deepLink && (
        <a href={deepLink} target="_blank" rel="noopener noreferrer">
          <MaterialIcon name="open_in_new" size={16} />
        </a>
      )}
    </Space>
  );

  return (
    <Card title={cardTitle} extra={cardExtra} size="small" style={{ marginBottom: 16 }}>
      {metrics && metrics.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: children ? 12 : 0 }}>
          {metrics.map((m) => (
            <div key={m.label}>
              <Text type="secondary" style={{ fontSize: 12 }}>{m.label}</Text>
              <div>
                <Text strong>{formatMetricValue(m.value, m.unit)}</Text>
                {m.quota != null && (
                  <>
                    <Text type="secondary"> / {formatMetricValue(m.quota, m.unit)}</Text>
                    <Progress
                      percent={Math.min(100, Math.round((Number(m.value) / m.quota) * 100))}
                      size="small"
                      strokeColor={alertConfig[m.alertLevel || "ok"].color}
                      showInfo={false}
                      style={{ marginTop: 4 }}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {children}
    </Card>
  );
}
